import { Box, Grid, Typography } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import PaymentButton from '@acorn-portal/shared/src/components/buttons/PaymentButton';
import PriceBreakdown from '@acorn-portal/shared/src/components/display/price-breakdown';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { handTakeUpMidTermAdjustment } from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useNavigate } from 'react-router';
import { MtaPaymentCollectionOption, PaymentMethods } from '@acorn-portal/shared/src/services/api';
import { flipToPositive, formatPounds, isProtect, isProtectYD } from '@acorn-portal/shared/src/utils';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
interface IProps {
	setLoading: (loading: boolean) => void;
	financeOption: MtaPaymentCollectionOption;
}

const FinanceRefundOption = ({ setLoading, financeOption }: IProps) => {
	const midTermAdjustment = useAppSelector((redux) => redux.midTermAdjustment);
	const policy = useAppSelector((redux) => redux.fullPolicy);
	const contactPreferences = useAppSelector((redux) => redux.contactPreferences);
	const midTermAdjustmentResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);
	const navigate = useNavigate();

	const isProtectYoungDriver: boolean = isProtectYD(policy.product, policy.scheme ?? '');
	const isProtectAdultDriver: boolean = isProtect(policy.product, policy.scheme ?? '');

	const submitPaymentForm = async () => {
		setLoading(true);
		try {
			await handTakeUpMidTermAdjustment({
				policyReference: midTermAdjustment.policyReference,
				sendPostalDocuments: contactPreferences.printToPost,
				transactionId: midTermAdjustment.transactionId,
				takeUpDetails: {
					underwritingResponseId: midTermAdjustmentResponse.underwritingOutcomeResponseId,
					underwritingOptionId: midTermAdjustmentResponse.optionId,
					paymentCollectionOptionCode: financeOption.code,
					selectedBalanceSettlementMethod: PaymentMethods.DIRECT_DEBIT,
				},
			});
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangePaymentSuccess);
		} catch (error) {
			console.error('Error processing payment:', error);
			navigate(ROUTE_PATHS.error);
			setLoading(false);
		}
	};

	return (
		<>
			<Grid
				container
				maxWidth='md'
				margin='auto'
				sx={{
					border: 1,
					borderColor: 'grey.300',
					p: 2,
					mb: 4,
					borderRadius: 2,
				}}
			>
				<Grid container sx={{ flexDirection: 'column' }}>
					<Grid sx={{ textAlign: { xs: 'left' } }}>
						<Typography component='h3' color='primary'>
							Adjust Remaining Instalments
						</Typography>
						<Typography fontSize={44} fontWeight='bold' color='primary' lineHeight={1}>
							{formatPounds(flipToPositive(financeOption.totalAmountPayable ?? 0))}
						</Typography>
						<Grid flex={1}>
							<Typography fontSize={16}>Refund Amount</Typography>
						</Grid>

						<Grid container marginTop={2} gap={2}>
							{financeOption.unequalInstalmentAmount && financeOption.unequalInstalmentAmount > 0 ? (
								<Grid sx={{ minWidth: '22%' }}>
									<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>Next instalment</Typography>
									<Typography sx={{ fontSize: 14 }}>
										{formatPounds(flipToPositive(financeOption.unequalInstalmentAmount ?? 0))}
									</Typography>
									<Grid sx={{ minWidth: '22%' }}>
										<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>
											{financeOption.appliedInstalmentCount &&
												financeOption.appliedInstalmentCount > 1 &&
												financeOption.appliedInstalmentCount - 1}{' '}
											Remaining instalments
										</Typography>
										<Typography sx={{ fontSize: 14 }}>
											{formatPounds(flipToPositive(financeOption.standardInstalmentAmount ?? 0))}
										</Typography>
									</Grid>
								</Grid>
							) : (
								<Grid sx={{ minWidth: '22%' }}>
									<Typography sx={{ fontSize: 12, height: { xs: 'auto', sm: 'auto' } }}>
										{financeOption.appliedInstalmentCount &&
											financeOption.appliedInstalmentCount > 1 &&
											financeOption.appliedInstalmentCount}{' '}
										Remaining instalments
									</Typography>
									<Typography sx={{ fontSize: 14 }}>
										{formatPounds(flipToPositive(financeOption.standardInstalmentAmount ?? 0))}
									</Typography>
								</Grid>
							)}
						</Grid>

						<PriceBreakdown
							price={financeOption.totalAmountPayable}
							fee={midTermAdjustmentResponse.coverDetails.fee}
							product={policy.product}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Box
				maxWidth='md'
				margin='auto'
				sx={(theme) => ({
					paddingTop: 3,
					paddingBottom: 3,
					marginTop: 2,
					marginBottom: 2,
					borderRadius: 2,
					border: `1px solid ${theme.palette.divider}`,
					backgroundColor: `${theme.palette.background.paper}`,
					boxShadow: `0 4px 12px ${theme.palette.primary.main}10`,
					padding: 3,
					position: 'relative',
					'&::before': {
						content: '""',
						position: 'absolute',
						top: 0,
						left: 0,
						width: '100%',
						height: '4px',
						background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
						borderTopLeftRadius: 8,
						borderTopRightRadius: 8,
					},
				})}
			>
				<Typography fontSize={14} marginTop={2} sx={{ pt: 2 }}>
					{isProtectYoungDriver
						? ''
						: 'Please be aware an interest rate applies. For further details, please review your Advance notice found on your portal.'}
				</Typography>
				<Typography fontSize={14} marginTop={2}>
					No payment will be taken now. The total cost will be spread across your remaining instalments.
				</Typography>
				<Typography fontSize={14} marginTop={2} marginBottom={2}>
					By selecting the option to add this to your{' '}
					{isProtectAdultDriver ? `finance agreement` : `Direct Debit payments`}, you are confirming that your new
					monthly payments are affordable now and for the duration of the agreement and that you have budgeted to ensure
					that you will be able to make those monthly repayments.
				</Typography>
				<ActionButtons>
					<BackButton onClick={() => navigate(-1)}>Back</BackButton>
					<PaymentButton onClick={submitPaymentForm} icon='Bank'>
						Process Refund
					</PaymentButton>
				</ActionButtons>
			</Box>
		</>
	);
};
export default FinanceRefundOption;
